/**
 * A compact "set · #number · variant" caption for telling two same-name cards apart on the customer
 * browse surfaces (#530, variant tag #540), such as two "Gengar"s at different prices that differ
 * by printing. Returns `null` when none of set, number or variant is known (a generic or
 * uncatalogued unit, or a catalog miss), so a caller renders nothing rather than an empty or lone
 * "·" line.
 *
 * `variantLabel` (such as "Reverse Holo", "1st Edition" or "Promo") is the short printing or finish
 * tag, appended after set and number and shown as-is (no leading "#"). It composes with set and
 * number, so two units identical but for finish read distinctly, and stands on its own when set
 * and number are unknown. Known printings include Non-Holo; a blank or absent tag is the common
 * case and adds nothing.
 */
export function formatCardIdentity(
  setName: string | null | undefined,
  number: string | null | undefined,
  variantLabel?: string | null,
): string | null {
  const set = setName?.trim() || null;
  // The collector number is shown with a leading "#"; a set that already reads on its own needs no
  // marker. Guard against a number that already carries its own "#" so it never doubles up.
  const rawNum = number?.trim() || null;
  const num = rawNum ? (rawNum.startsWith("#") ? rawNum : `#${rawNum}`) : null;
  const variant = variantLabel?.trim() || null;
  const parts = [set, num, variant].filter((p): p is string => p != null);
  return parts.length ? parts.join(" · ") : null;
}

/** What an inventory row prints on its two lines. `subtitle` is `null` when there is no second. */
export type RowCaption = { title: string; subtitle: string | null; };

/**
 * The words that describe the shape of a sealed package and nothing else about it.
 *
 * A name built only from these is a product TYPE, not a product: every set has an "Elite Trainer
 * Box", so the name identifies nothing on its own. One unfamiliar word is enough to make a name
 * particular ("Mega Moonlit Tin: Mega Clefable Ex", "Maleficent [Chase] #232"), which is the safe
 * direction to fail in — an unrecognised name keeps the row exactly as it reads today.
 *
 * A digit is not shape, though it looks like the count in "3-Pack Blister". Sets are named with
 * numbers too, and counting digits as shape swapped all eight of "151 Elite Trainer Box" and its
 * siblings, whose names already say which set they are. Losing the blister is the cheaper half of
 * that trade: it keeps the row it has today rather than getting a wrong one.
 */
const BOX_SHAPE_WORDS = new Set([
  "battle",
  "blister",
  "booster",
  "box",
  "build",
  "bundle",
  "case",
  "checklane",
  "collection",
  "deck",
  "display",
  "elite",
  "half",
  "jumbo",
  "mini",
  "pack",
  "premium",
  "promotion",
  "sleeved",
  "super",
  "tin",
  "trainer",
]);

/** Whether a name describes a kind of package rather than a particular one. */
function namesOnlyABoxShape(productName: string): boolean {
  const words = productName.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  return words.length > 0 && words.every((word) => BOX_SHAPE_WORDS.has(word));
}

/**
 * Which of a unit's two names leads the row, and which reads underneath (TRO-1223).
 *
 * Normally the product's own name leads and the set reads below it as the "set · #number" caption
 * `formatCardIdentity` composes. That is right whenever the name identifies the thing, which
 * covers every card and all but a handful of sealed products.
 *
 * The exception is a sealed product named for its shape alone. A box bought by barcode resolves
 * through PriceCharting, which names it "Elite Trainer Box" and carries the set in a separate
 * field, so four different boxes read as four identical rows. There the set leads and the shape
 * reads as the subtitle.
 *
 * Keyed on the name's own words rather than on `product_type`, because almost no sealed product is
 * in that position: 14 of the catalog's 8,172 distinct sealed names are a bare shape. Swapping on
 * `product_type` alone would demote "Maleficent [Chase] #232" under "Funko POP Disney" to help
 * "Elite Trainer Box".
 *
 * A box whose set nothing names keeps its shape on top, because the alternative is an untitled row.
 */
export function formatRowCaption(
  productName: string,
  setName: string | null | undefined,
  collectorNumber: string | null | undefined,
  productType: string | null | undefined,
): RowCaption {
  const set = setName?.trim() || null;
  if (productType === "sealed" && set && namesOnlyABoxShape(productName)) {
    return { title: set, subtitle: productName };
  }
  return { title: productName, subtitle: formatCardIdentity(set, collectorNumber) };
}

/**
 * The slab a unit is in, as the seal a `CardTile` stamps on its art: "PSA 10", or the bare "GRADED"
 * for a slab whose grader or grade was never recorded. `null` for a raw copy, so the tile draws no
 * seal at all rather than stamping "Raw" on the art.
 *
 * One formatter, like `formatCardIdentity` above, so a unit's seal reads identically in the stock
 * grid, on the storefront, and on its own card-detail page (#760). A card that loses its seal on
 * the way to its own page reads as a different object.
 */
export function formatGradeLabel(
  gradingCompany: string | null | undefined,
  grade: string | null | undefined,
  isGraded?: boolean | null,
): string | null {
  if (gradingCompany && grade) return `${gradingCompany} ${grade}`;
  return isGraded ? "GRADED" : null;
}

/**
 * The same seal, kept in parts as well as spelled out, which a `CardTile` needs once the seal is
 * drawn as a slab rather than printed as a chip (TRO-627).
 *
 * `formatGradeLabel` above answers what the slab says, which is all a list row's chip needs. A slab
 * plate also needs to know whose plate it is (to pick the grader's mark) and what number goes in
 * the corner, which the joined string cannot answer without being parsed apart. Splitting a
 * formatted label on its space would break on the first grader whose name contains one.
 *
 * `label` is still carried so the two renderings can never disagree, and so a caller that only
 * wants the chip has it without formatting the same fields twice. `formatGradeSeal` returns `null`
 * for a raw copy, as `formatGradeLabel` does: a raw card gets no seal, not an empty one.
 */
export type GradeSeal = {
  /** The grading company as recorded, or `null` for a slab whose grader we never captured. */
  company: string | null;
  /** The grade as recorded, or `null` alongside a `null` company for a bare "GRADED" slab. */
  grade: string | null;
  /** "PSA 10", or "GRADED". */
  label: string;
};

export function formatGradeSeal(
  gradingCompany: string | null | undefined,
  grade: string | null | undefined,
  isGraded?: boolean | null,
): GradeSeal | null {
  const label = formatGradeLabel(gradingCompany, grade, isGraded);
  if (label === null) return null;
  if (gradingCompany && grade) return { company: gradingCompany, grade, label };
  return { company: null, grade: null, label };
}

/**
 * Why a graded unit shows no market value (TRO-143), as copy, or `null` when there is nothing to
 * explain, which covers every raw unit and every unit that was priced.
 *
 * ADR-0054 keeps a slab that cannot be priced unpriced rather than quoting its raw printing's
 * price, because a PSA 10 is not worth what the raw card is. `formatMoney` renders any absent
 * amount as an em dash (see `money.ts`: unknown is not zero), which suits a transient miss but not
 * this case: the operator cannot tell a permanent coverage gap from a failure, so a deliberate
 * decision reads as a bug.
 *
 * `short` fits a list row's one clipped line; `long` is for a detail surface with room. The last
 * two reasons are worded differently on purpose:
 *
 * - `asked_and_empty`: the graded source was consulted and has no price at this grade, so saying
 *   there is no graded market is a tested claim.
 * - `unreachable`: no graded source could be consulted for this copy. It must not read as "no
 *   graded market exists", because the alias can be missing or the provider unavailable even when
 *   a market exists.
 */
export function formatUnpricedGradedReason(
  reason: string | null | undefined,
): { short: string; long: string; } | null {
  if (reason === "missing_grade") {
    return {
      short: "Grade needed",
      long: "Confirm the grader and grade to check its market value",
    };
  }
  if (reason === "unsupported_grade") {
    return {
      short: "Grade not covered",
      long: "The price source does not cover this grader and grade",
    };
  }
  if (reason === "missing_mapping") {
    return {
      short: "Price link unavailable",
      long: "No verified price-source match for this printing",
    };
  }
  if (reason === "provider_price_missing") {
    return { short: "No grade price", long: "The price source has no value for this grade" };
  }
  if (reason === "asked_and_empty") {
    return { short: "No graded market", long: "No graded market for this grade" };
  }
  if (reason === "unreachable") {
    return { short: "Graded price n/a", long: "No graded price available for this copy" };
  }
  return null;
}

/** Remove only a trailing collector-number tag that matches the separate number field. */
export function cardNameWithoutNumber(name: string, number?: string | null): string {
  const value = number?.trim().replace(/^#/, "");
  if (!value) return name;
  const title = name.trim();
  for (const suffix of [` (${value})`, ` (#${value})`, ` #${value}`]) {
    if (title.endsWith(suffix)) return title.slice(0, -suffix.length).trim() || name;
  }
  return name;
}

/** Use a real, repeatable example from the selected set, independent of display order. */
export function catalogSearchPlaceholder(
  cards: readonly { name?: string | null; number?: string | null; }[],
): string {
  const examples = cards.map(card => ({
    name: cardNameWithoutNumber(card.name ?? "", card.number).split(" - ")[0].trim(),
    number: card.number?.trim().replace(/^#/, "") ?? "",
  })).filter(card => card.name && card.number && card.name.length <= 20);
  examples.sort((a, b) =>
    a.number.localeCompare(b.number, "en", { numeric: true })
    || a.name.localeCompare(b.name, "en")
  );
  const example = examples[0];
  return example
    ? `Search e.g. ${example.name} or ${example.number}`
    : "Search by name or collector number";
}
